"use client";

import { type FormEvent, useState } from "react";
import { Notice, PillGroup, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  revealField,
  WordRows,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  answeredParam,
  normalizeLookup,
  type PartOfSpeech,
} from "@/lib/tool-kit/vocabulary/datamuse";
import {
  posCounts,
  type ReverseEntry,
  reverseEntries,
  reverseQuery,
  startLetters,
  topicWords,
} from "@/lib/tool-kit/vocabulary/reverse-dictionary";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";

/** English strings. A translated content file overrides these through `ui`. */
export const REVERSE_DICTIONARY_STRINGS = {
  descriptionLabel: "Describe the word you are looking for",
  descriptionPlaceholder: "a person who studies the stars",
  descriptionHint:
    "A definition, a phrase, or a few related words. Press Enter or choose Find words.",
  topicLabel: "Topic (optional)",
  topicPlaceholder: "astronomy",
  topicHint:
    "Up to five words that nudge the ranking toward a subject — they reorder the results rather than filter them.",
  startLabel: "Starts with (optional)",
  startPlaceholder: "b",
  startHint: "If you remember the first letter or two, add them here.",
  find: "Find words",
  clear: "Clear",
  tryLabel: "Try:",
  examples: [
    "fear of small enclosed spaces",
    "the main argument of an essay",
    "a word that sounds like what it means",
    "to make something less severe",
  ],
  emptyPrompt:
    "Describe a word you cannot quite remember and see the words that fit, best match first.",
  heading: "Words for “{description}”",
  topicNote: "Ranked toward the topic “{topics}”.",
  startNote: "Starting with “{letters}”.",
  wordCount: "one:{count} word|other:{count} words",
  rankHint:
    "Best guesses first, each with its definition. Click a word to look for words close to it.",
  filterLabel: "Part of speech",
  filterAll: "All",
  filterOption: "{label} ({count})",
  posGroups: {
    n: "Nouns",
    v: "Verbs",
    adj: "Adjectives",
    adv: "Adverbs",
    u: "Other",
  } as Record<PartOfSpeech, string>,
  posNames: {
    n: "noun",
    v: "verb",
    adj: "adjective",
    adv: "adverb",
    u: "other",
  } as Record<PartOfSpeech, string>,
  noResults:
    "No words matched that description. Try fewer words, plainer phrasing, or the key idea on its own.",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  copy: "Copy",
  copied: "Copied!",
  copyWord: "Copy “{word}”",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof REVERSE_DICTIONARY_STRINGS;
type Filter = "all" | PartOfSpeech;

interface Search {
  description: string;
  topics: string;
  start: string;
}

const FIELD_ID = "revdict-description";
const MAX_DESCRIPTION = 200;
const MAX_TOPIC_INPUT = 80;
const MAX_START = 10;
const RE_LETTER = /\p{L}/u;

function Results({
  description,
  topics,
  start,
  entries,
  onSelect,
  stale,
  strings,
}: {
  description: string;
  topics: string;
  start: string;
  entries: readonly ReverseEntry[];
  onSelect: (word: string) => void;
  stale: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [filter, setFilter] = useState<Filter>("all");
  if (entries.length === 0) {
    return <Notice>{strings.noResults}</Notice>;
  }
  const counts = posCounts(entries);
  const active = counts.some((item) => item.pos === filter) ? filter : "all";
  const shown = entries.filter(
    (entry) => active === "all" || entry.pos === active
  );
  return (
    <div className={stale ? "space-y-5 opacity-60" : "space-y-5"}>
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{t(strings.heading, { description })}</h2>
        <p className={HINT}>
          {tPlural(strings.wordCount, locale, entries.length, {
            count: fmt(entries.length),
          })}
          {topics ? ` · ${t(strings.topicNote, { topics })}` : null}
          {start ? ` · ${t(strings.startNote, { letters: start })}` : null}
        </p>
      </div>
      {counts.length > 1 ? (
        <PillGroup
          label={strings.filterLabel}
          name="revdict-pos"
          onChange={setFilter}
          options={[
            { value: "all" as Filter, label: strings.filterAll },
            ...counts.map((item) => ({
              value: item.pos as Filter,
              label: t(strings.filterOption, {
                label: strings.posGroups[item.pos],
                count: fmt(item.count),
              }),
            })),
          ]}
          value={active}
        />
      ) : null}
      <p className={HINT}>{strings.rankHint}</p>
      <WordRows
        copyStrings={strings}
        onSelect={onSelect}
        rows={shown.map((entry) => ({
          word: entry.word,
          badge: strings.posNames[entry.pos],
          detail: entry.definition ?? undefined,
        }))}
      />
    </div>
  );
}

export default function ReverseDictionaryWidget() {
  const strings = useToolUi(REVERSE_DICTIONARY_STRINGS);
  const [description, setDescription] = useState("");
  const [topics, setTopics] = useState("");
  const [start, setStart] = useState("");
  const [search, setSearch] = useState<Search | null>(null);
  const lookup = useDatamuse(
    search
      ? [reverseQuery(search.description, search.topics, search.start)]
      : [],
    0
  );
  const answeredDescription = answeredParam(lookup.answered, "ml");
  const answeredTopics = answeredParam(lookup.answered, "topics").replaceAll(
    ",",
    ", "
  );
  const answeredStart = answeredParam(lookup.answered, "sp").replace("*", "");
  const answer = search ? lookup.results[0] : undefined;

  function run(nextDescription: string, nextStart: string) {
    const normalized = normalizeLookup(nextDescription);
    if (!RE_LETTER.test(normalized)) {
      return;
    }
    setSearch({
      description: normalized,
      topics: topicWords(topics).join(" "),
      start: startLetters(nextStart),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    run(description, start);
  }

  /** A clicked result becomes the next description; a first-letter filter would hide it. */
  function lookUp(word: string) {
    setDescription(word);
    setStart("");
    run(word, "");
    revealField(FIELD_ID);
  }

  function clear() {
    setDescription("");
    setTopics("");
    setStart("");
    setSearch(null);
  }

  return (
    <>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <TextField
          hint={strings.descriptionHint}
          id={FIELD_ID}
          label={strings.descriptionLabel}
          onChange={(value) => setDescription(value.slice(0, MAX_DESCRIPTION))}
          placeholder={strings.descriptionPlaceholder}
          value={description}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.topicHint}
            id="revdict-topic"
            label={strings.topicLabel}
            onChange={(value) => setTopics(value.slice(0, MAX_TOPIC_INPUT))}
            placeholder={strings.topicPlaceholder}
            value={topics}
          />
          <TextField
            hint={strings.startHint}
            id="revdict-start"
            label={strings.startLabel}
            onChange={(value) => setStart(value.slice(0, MAX_START))}
            placeholder={strings.startPlaceholder}
            value={start}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={!RE_LETTER.test(description)}
            type="submit"
          >
            {strings.find}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!(description || topics || start || search)}
            onClick={clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </form>
      {search ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompt}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples}
          />
        </div>
      )}
      {answer ? (
        <Results
          description={answeredDescription}
          entries={reverseEntries(answeredDescription, answer)}
          key={lookup.answered}
          onSelect={lookUp}
          stale={lookup.status === "loading"}
          start={answeredStart}
          strings={strings}
          topics={answeredTopics}
        />
      ) : null}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
